// Home, signed in: what works right now (Home.dc.html, «Health status: show what's working, not a
// to-do»). Built from what the site already knows - connections, saved keys, assistants - without
// calling the sources; sources never connected are not listed.
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { getConnections, getDevices, getSecrets, getSiteClients, type Connections, type Device, type SecretsMap } from "../api";
import { defaultClient } from "../client";
import { t, type MessageKey } from "../i18n";
import { formatWhen } from "../lib";
import { guideAnchor, type GuideId } from "../guides";
import { UpdateCardButton } from "./PlanCard";

export interface HealthRow {
  id: GuideId;
  title: string;
  /** Working: the short note; needs a look: the lowercase reason. */
  note: string;
  ok: boolean;
}

/** The rows of the card: connected sources only, working first, then those that need a look. */
export function healthRows(conns: Connections | null, secrets: SecretsMap): HealthRow[] {
  const rows: HealthRow[] = [];
  const oauth = (id: GuideId, title: string, provider: "google" | "meta" | "tiktok" | "google_play" | "apple_ads") => {
    const c = conns?.[provider];
    if (!c?.connected) return;
    const reauth = ((c as { needs_reauthorization?: string[] }).needs_reauthorization ?? []).includes(id);
    rows.push({ id, title, ok: !reauth, note: t(reauth ? "home.health.permissions" : (`home.health.via.${provider}` as MessageKey)) });
  };
  oauth("meta", "Meta Ads", "meta");
  oauth("fb_pages", "Facebook Pages", "meta");
  oauth("instagram", "Instagram", "meta");
  oauth("ga4", "Analytics 4", "google");
  oauth("google_ads", "Google Ads", "google");
  oauth("search_console", "Search Console", "google");
  oauth("tiktok", "TikTok Ads", "tiktok");
  oauth("apple_ads", "Apple Ads", "apple_ads");
  oauth("google_play", "Google Play", "google_play");
  if (secrets.appsflyer_token?.set) rows.push({ id: "appsflyer", title: "AppsFlyer", ok: true, note: t("home.health.reports") });
  if (secrets.revenuecat_key?.set) rows.push({ id: "revenuecat", title: "RevenueCat", ok: true, note: t("home.health.secretKey") });
  if (secrets.app_store_connect_key?.set)
    rows.push({ id: "app_store_connect", title: "App Store Connect", ok: true, note: t("home.health.teamKey") });
  rows.push({ id: "apple", title: "App Store", ok: true, note: t("home.health.public") });
  return [...rows.filter((r) => r.ok), ...rows.filter((r) => !r.ok)];
}

const KIND: Record<Device["kind"], MessageKey> = {
  connector: "home.health.kind.connector",
  gpt: "home.health.kind.gpt",
  extension: "home.health.kind.extension",
};

/** `blocked`: the plan stops the answers (ADR-0045) - «subscribe» for an expired one, «card» for a paused one. */
export function HealthStatus({ timeZone, blocked = null }: { timeZone: string; blocked?: "subscribe" | "card" | null }) {
  const [conns, setConns] = useState<Connections | null>(null);
  const [secrets, setSecrets] = useState<SecretsMap>({});
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [clientName, setClientName] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // The card is about the default client (ADR-0062); its name is in the title only when there are several clients.
    void getSiteClients()
      .then((r) => {
        if (!alive || !Array.isArray(r?.clients) || r.clients.length < 2) return;
        setClientName(defaultClient(r.clients)?.name ?? null);
      })
      .catch(() => undefined);
    void Promise.allSettled([getConnections(), getSecrets(), getDevices()]).then(([c, s, d]) => {
      if (!alive) return;
      if (c.status === "fulfilled") setConns(c.value);
      if (s.status === "fulfilled") setSecrets(s.value.items ?? {});
      setDevices(d.status === "fulfilled" ? d.value.items : []);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const rows = blocked ? [] : healthRows(conns, secrets);
  const ok = rows.filter((r) => r.ok);
  const look = rows.filter((r) => !r.ok);
  const clients = [...(devices ?? [])].sort((a, b) => (b.last_used_at ?? "").localeCompare(a.last_used_at ?? ""));
  const when = (d: Device) => (d.last_used_at ? formatWhen(d.last_used_at, timeZone) : t("home.health.never"));

  const row = (r: HealthRow) => (
    <Link key={r.id} className="health-row" to={`/connections#${guideAnchor(r.id)}`}>
      <span className={r.ok ? "health-dot on" : "health-dot look"} aria-hidden="true" />
      <span>{r.title}</span>
      <span className={r.ok ? "health-note" : "health-note look"}>{r.note}</span>
    </Link>
  );

  return (
    <aside className="card elev-md health-card" aria-label={t("home.health.title")} data-testid="health-status">
      <div className="health-head">
        <span className="group-label">{clientName ? t("home.health.titleClient", { client: clientName }) : t("home.health.title")}</span>
        {blocked ? (
          <span className="health-count look" data-testid="health-blocked">
            {blocked === "subscribe" ? (
              <Link to="/subscribe">{t("home.health.blockedSubscribe")}</Link>
            ) : (
              <UpdateCardButton label={t("home.health.blockedCard")} className="text-link" />
            )}
          </span>
        ) : (
          <span className="health-count">{loaded ? t(ok.length === 1 ? "home.health.live1" : "home.health.live", { n: ok.length }) : "…"}</span>
        )}
      </div>
      {loaded && ok.map(row)}
      {loaded && look.length > 0 && (
        <>
          <div className="group-label health-look-label">{t("home.health.needsLook")}</div>
          {look.map(row)}
        </>
      )}
      {loaded && (
        <div className="health-foot" data-testid="health-clients">
          {clients.length === 0 ? (
            <span>
              {t("home.health.noClient")} <Link to="/install">{t("home.health.install")}</Link>
            </span>
          ) : clients.length === 1 ? (
            <span className="health-client-one">
              <span className="health-dot on small" aria-hidden="true" />
              <span>
                {t("home.health.oneClient", { client: clients[0]!.client_name, kind: t(KIND[clients[0]!.kind]), when: when(clients[0]!) })}
              </span>
            </span>
          ) : (
            <>
              <div className="health-clients-head">
                <span className="group-label">{t("home.health.askingFrom")}</span>
                <span>{t("home.health.lastRequest")}</span>
              </div>
              {clients.map((d) => (
                <div key={d.id} className="health-client">
                  <span className="health-dot on small" aria-hidden="true" />
                  <span>
                    {d.client_name} <span className="health-kind">· {t(KIND[d.kind])}</span>
                  </span>
                  <span>{when(d)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </aside>
  );
}
